/**
 * InfraPricingForm — create dialog body for `/admin/pricing/infra`.
 *
 * Escopo (Story 7.31): só a VM de produção (AWS Lightsail, 2GB RAM/2vCPU) —
 * descrição livre com default sugerido, valor mensal fixo. Preço é sempre
 * cadastro manual (Constitution Art. IV).
 */

import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface InfraPricingFormValue {
  description: string;
  monthlyValue: number | null;
}

export const DEFAULT_INFRA_PRICING_FORM: InfraPricingFormValue = {
  description: 'VM Lightsail (2GB RAM / 2vCPU) — sales-ia.reinoeducacao.com',
  monthlyValue: null,
};

interface Props {
  submitting?: boolean;
  onSubmit: (next: { description: string; monthlyValue: number }) => void;
  onCancel: () => void;
}

export function InfraPricingForm({ submitting, onSubmit, onCancel }: Props) {
  const [form, setForm] = useState<InfraPricingFormValue>(DEFAULT_INFRA_PRICING_FORM);

  const isValid =
    form.description.trim().length > 0 && form.monthlyValue !== null && form.monthlyValue > 0;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isValid || form.monthlyValue === null) return;
    onSubmit({ description: form.description.trim(), monthlyValue: form.monthlyValue });
  }

  const disabled = !isValid || !!submitting;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="infra-pricing-description">Descrição</Label>
        <Input
          id="infra-pricing-description"
          aria-label="Descrição"
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="infra-pricing-value">Valor mensal (USD)</Label>
        <Input
          id="infra-pricing-value"
          aria-label="Valor mensal (USD)"
          type="number"
          step={0.01}
          min={0}
          value={form.monthlyValue ?? ''}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              monthlyValue: e.target.value === '' ? null : Number(e.target.value),
            }))
          }
        />
      </div>

      <p className="text-xs text-muted-foreground">
        Vale a partir de agora. Preço antigo não é sobrescrito — fica no histórico. Cadastre de novo
        só se o plano/valor da VM mudar.
      </p>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={!!submitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={disabled}>
          Cadastrar custo
        </Button>
      </div>
    </form>
  );
}
